/**
 * ChannelsPanel — Amazon account health, Shopify funnel, showrooms,
 * wholesale program.
 */

import { toast } from '@/hooks/useToast';
import { AlertTriangle, CheckCircle2, Lock, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Meter, PanelSection } from '@/components/game/ui';
import { useGameStore } from '@/store/gameStore';
import { money, num, pct, formatGameDate } from '@/game/format';
import {
  avgRating,
  outboundParcelPerUnit,
  fulfillmentPerUnit,
  shopifyConversion,
  showroomDemandFactor,
} from '@/game/economy';
import { priceScore } from '@/game/products';
import {
  SHOWROOM_CITIES,
  SHOWROOM_DAILY_COST,
  SHOWROOM_DEMAND_BONUS,
  SHOWROOM_MAX_LOCATIONS,
  SHOWROOM_OPEN_COST,
  SHOWROOM_UPGRADE_COST,
  SHOWROOM_WHOLESALE_BONUS,
} from '@/game/constants';
import { has } from '@/game/research';
import { TUNING } from '@/game/constants';

export function ChannelsPanel() {
  const state = useGameStore((s) => s.state)!;
  const openShowroom = useGameStore((s) => s.openShowroom);
  const upgradeShowroom = useGameStore((s) => s.upgradeShowroom);

  const act = state.products.filter((p) => p.status === 'active');
  const priceAvg = act.length ? act.reduce((a, p) => a + priceScore(state, p), 0) / act.length : 1;
  const cvr = shopifyConversion(state, priceAvg);
  const organicSessions =
    TUNING.shopifyOrganicPerAwareness * Math.pow(state.player.awareness, 1.15) * (state.research.completed.includes('certipur') ? 1.1 : 1);
  const wh = state.channels.wholesale;

  const showroomUnlocked = has(state, 'showroom-pilot');
  const rooms = state.showrooms ?? [];
  const usedCities = new Set(rooms.map((r) => r.city));
  const nextCity = SHOWROOM_CITIES.find((c) => !usedCities.has(c.city));
  const demandBonus = Math.min(0.4, rooms.reduce((a, r) => a + SHOWROOM_DEMAND_BONUS[r.tier], 0));
  const roomDaily = rooms.reduce((a, r) => a + SHOWROOM_DAILY_COST[r.tier], 0);

  return (
    <div className="space-y-6">
      {/* Amazon */}
      <PanelSection title="Amazon (Seller Central + FBA)" desc="Your volume engine. Rank is a flywheel: velocity builds it, stockouts destroy it.">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              {state.channels.amazon.suspended ? (
                <><AlertTriangle className="h-4 w-4 text-red-500" /> Account suspended</>
              ) : (
                <><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Active</>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
            <Meter label="Organic rank strength" value={state.channels.amazon.organicRankScore} colorClass="bg-orange-500" />
            <Meter label={`Account health (${state.channels.amazon.sellerRating.toFixed(0)}/100)`} value={state.channels.amazon.sellerRating} colorClass={state.channels.amazon.sellerRating > 75 ? 'bg-emerald-500' : 'bg-red-500'} />
            <div className="text-sm text-muted-foreground">
              Referral fee {pct(state.channels.amazon.feePercent, 1)} + FBA {money(fulfillmentPerUnit(state))}/unit
            </div>
            <div className="text-sm text-muted-foreground">Category rating: ★ {avgRating(state).toFixed(2)} blended</div>
          </CardContent>
        </Card>
      </PanelSection>

      {/* Shopify */}
      <PanelSection title="Shopify DTC Store" desc="Owned audience. Better margin per unit, but you pay for every session.">
        <Card>
          <CardContent className="grid gap-x-8 gap-y-3 py-4 sm:grid-cols-2">
            <MetricRow label="Effective conversion" value={pct(cvr, 2)} />
            <MetricRow label="Est. organic sessions/day" value={num(organicSessions)} />
            <MetricRow label="Outbound shipping/unit" value={`${money(outboundParcelPerUnit(state))} (you eat it — "free shipping")`} />
            <MetricRow label="Payment processing" value={pct(state.channels.shopify.paymentFeePercent, 1)} />
          </CardContent>
        </Card>
      </PanelSection>

      {/* Showrooms */}
      <PanelSection
        title="Showrooms"
        desc="Physical retail halo: each location lifts ALL channel demand and builds trust — at painful fixed cost."
        right={
          showroomUnlocked && (
            <Button
              size="sm"
              disabled={!nextCity || rooms.length >= SHOWROOM_MAX_LOCATIONS || state.player.cash < SHOWROOM_OPEN_COST}
              onClick={() => {
                const err = openShowroom();
                if (err) toast({ title: 'Cannot open showroom', description: err, variant: 'destructive' });
              }}
            >
              <MapPin className="mr-1.5 h-4 w-4" />
              Open {nextCity ? nextCity.city.split(',')[0] : ''} — {money(SHOWROOM_OPEN_COST)}
            </Button>
          )
        }
      >
        {!showroomUnlocked ? (
          <Card className="border-dashed">
            <CardContent className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
              <Lock className="h-4 w-4" /> Locked — complete “Showroom Pilot Program” in Research.
            </CardContent>
          </Card>
        ) : (
          <>
            {rooms.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                <MiniStat label="Locations" value={`${rooms.length}/${SHOWROOM_MAX_LOCATIONS}`} />
                <MiniStat label="Demand bonus" value={`+${Math.round(demandBonus * 100)}%`} good />
                <MiniStat label="Daily overhead" value={money(roomDaily)} />
              </div>
            )}
            <div className="space-y-2">
              {rooms.map((r) => (
                <Card key={r.id}>
                  <CardContent className="flex flex-wrap items-center justify-between gap-3 py-3">
                    <div>
                      <p className="text-sm font-medium">{r.city} <Badge variant="outline" className="ml-1">Tier {r.tier}</Badge></p>
                      <p className="text-xs text-muted-foreground">
                        +{Math.round(SHOWROOM_DEMAND_BONUS[r.tier] * 100)}% demand · {money(SHOWROOM_DAILY_COST[r.tier])}/day · since {formatGameDate(r.openedDay)}
                      </p>
                    </div>
                    {r.tier < 3 ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={state.player.cash < SHOWROOM_UPGRADE_COST[(r.tier + 1) as 2 | 3]}
                        onClick={() => {
                          const err = upgradeShowroom(r.id);
                          if (err) toast({ title: 'Remodel failed', description: err, variant: 'destructive' });
                        }}
                      >
                        Remodel to T{r.tier + 1} — {money(SHOWROOM_UPGRADE_COST[(r.tier + 1) as 2 | 3])}
                      </Button>
                    ) : (
                      <Badge variant="secondary">Flagship</Badge>
                    )}
                  </CardContent>
                </Card>
              ))}
              {rooms.length === 0 && (
                <Card className="border-dashed">
                  <CardContent className="py-8 text-center text-sm text-muted-foreground">
                    No locations yet. {nextCity ? `First stop: ${nextCity.city} — ${nextCity.blurb}` : ''}
                  </CardContent>
                </Card>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Each location also adds +{Math.round(SHOWROOM_WHOLESALE_BONUS * 100)}% wholesale order size and up to +8 reputation drift at scale.
            </p>
          </>
        )}
      </PanelSection>

      {/* Wholesale */}
      <PanelSection
        title="Wholesale & Retail Partners"
        desc="Unlock via the Wholesale & Retail Program research. Bulk orders, thinner margins, net-60 payment."
      >
        {!wh.enabled ? (
          <Card className="border-dashed">
            <CardContent className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
              <Lock className="h-4 w-4" /> Locked — complete “Wholesale &amp; Retail Program” in Research.
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="grid gap-x-8 gap-y-3 py-4 sm:grid-cols-2">
              <MetricRow label="Contract retailers" value={num(wh.contractRetailers)} />
              <MetricRow label="Quarterly demand" value={`~${num(wh.quarterlyDemandUnits)} units`} />
              <MetricRow label="Discount off retail" value={pct(wh.discountOffRetail, 0)} />
              <MetricRow
                label="Receivables outstanding"
                value={
                  wh.receivables > 0
                    ? `${money(wh.receivables)} — collects ${formatGameDate(wh.nextCollectionDay)}`
                    : '$0'
                }
              />
            </CardContent>
          </Card>
        )}
        {wh.enabled && wh.receivables > 0 && (
          <Badge variant="outline" className="mt-2">Net-60 terms: cash arrives ~60 days after each booking. Plan inventory buys accordingly.</Badge>
        )}
      </PanelSection>
    </div>
  );
}

function MetricRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b pb-2 last:border-0 sm:border-0 sm:pb-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-right text-sm font-medium tabular-nums">{value}</span>
    </div>
  );
}

function MiniStat({ label, value, good }: { label: string; value: string; good?: boolean }) {
  return (
    <div className={cn('rounded-lg border bg-card p-2 text-center', good && 'border-emerald-500/40')}>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={cn('text-sm font-semibold tabular-nums', good && 'text-emerald-600 dark:text-emerald-400')}>{value}</p>
    </div>
  );
}
